import { AxiosResponse } from 'axios'
import { SELECTED_LANGUAGE_COOKIE_KEY_NAME } from '@config'
import { Cookies } from '@utils/cookies'
import { parseJson } from '@utils/string'
import { ForceAny } from '@utils/typescript'

export type UserStoredDetail = {
  email: string
  phoneNumber: string
  firstName: string
  lastName: string
  id: string
  profileUrl?: string
}

type UserApiEndpoint = `api/v1/user/${string}`

/** Use this key as params in the login endpoint to skip login */
export const DO_NOT_LOGIN_KEY = 'do-not-login'

export const loginEndpoint = 'api/v1/user/login' satisfies UserApiEndpoint
export const refreshTokenEndpoint =
  'api/v1/user/refresh-token' satisfies UserApiEndpoint
export const verifyEmailEndpoint =
  'api/v1/user/verify-identity' satisfies UserApiEndpoint

export const userProfileEndpoint = 'api/v1/user'

export const tokenApi = [
  verifyEmailEndpoint,
  loginEndpoint,
  userProfileEndpoint,
]
export const ACCESS_TOKEN = 'x-access-token'
export const REFRESH_TOKEN = 'x-access-refresh-token'
export const SUPRSEND_TOKEN = 'x-suprsend-access-token'
export const ACCEPT_LANGUAGE = SELECTED_LANGUAGE_COOKIE_KEY_NAME
export const USER_ID = 'x-userId'
export const USER_DETAIL_TOKEN = 'x-user-detail-token'

export const getUserId = () => Cookies.getCookie(USER_ID)
export const getUserData = (): Partial<UserStoredDetail> =>
  parseJson(
    Cookies.getCookie(USER_DETAIL_TOKEN) as string,
    {} as Partial<UserStoredDetail>
  )

export const getAccessToken = () => Cookies.getCookie(ACCESS_TOKEN)
export const getRefreshToken = () => Cookies.getCookie(REFRESH_TOKEN) ?? ''
/** @deprecated use state logged where it is used - this only serve general call purpose */
export const getIsLoggedIn = () => !!getUserId() && !!getAccessToken()

const setUserDataCookie = (userData: ForceAny) => {
  Cookies.eraseCookie(USER_DETAIL_TOKEN)
  Cookies.setCookie(
    USER_DETAIL_TOKEN,
    JSON.stringify({
      email: userData.email,
      phoneNumber: userData.phoneNumber,
      firstName: userData.firstName,
      lastName: userData.lastName,
      id: userData.id,
      profileUrl: userData.profileUrl,
    } as UserStoredDetail)
  )
}

export const updateUserDataCookie = (userData?: ForceAny) => {
  if (userData) {
    setUserDataCookie(userData)
  }
}

const userAuthTokens = [
  ACCESS_TOKEN,
  REFRESH_TOKEN,
  USER_ID,
  USER_DETAIL_TOKEN,
  SUPRSEND_TOKEN,
]

export const LOGOUT_EVENT = 'logout-event'
export const LOGIN_EVENT = 'login-event'

export const AUTH_EVENT_CHANNEL = 'auth-channel'

export const userSessionInactive = () => {
  userAuthTokens.forEach((tokenName) => {
    Cookies.eraseCookie(tokenName)
  })
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(LOGOUT_EVENT))
  }
}

export const userSessionActive = (
  headers: AxiosResponse['headers'],
  userData?: ForceAny
) => {
  if (userData) {
    setUserDataCookie(userData)
  }

  ;[ACCESS_TOKEN, REFRESH_TOKEN, SUPRSEND_TOKEN, USER_ID].forEach((name) => {
    const keyName = name.toLowerCase()
    const tokenValue = headers[keyName]
    if (tokenValue) {
      Cookies.eraseCookie(name)
      Cookies.setCookie(name, headers[keyName])
    }
  })

  if (typeof window !== 'undefined') {
    const channel = new BroadcastChannel(AUTH_EVENT_CHANNEL)
    channel.postMessage({
      type: LOGIN_EVENT,
      payload: userData,
    })
  }
}
